// @givmo/fundraisers-module — every line the layer screens show (FCM-WEB25), as Jonathan ruled them with
// the mockups (revision 2, final OK 2026-10-06 23:05 PDT; `scratchpad/2026-10-05-fcm-web25-layer-mockups/`).
// A refusal is never reworded here: the page shows the server's own words from its answer. The few server
// sentences a page shows BEFORE any refusal (the frames' tree foot, frozen line and attached-layer foot card)
// are copied verbatim from the backend, straight apostrophes and all, in `SERVER_WORDS`.

/** "Ana’s", or "Harbor Arts’" for a name ending in s. */
export const possessive = (name: string) => (name.endsWith('s') ? `${name}’` : `${name}’s`);

/** "A, B and C" (or "A, B or C"). */
export function series(items: readonly string[], last: 'and' | 'or' = 'and'): string {
  return items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} ${last} ${items[items.length - 1]}`;
}

/** "a campaign another organization runs" / "3 campaigns other organizations run". */
const runByOthers = (n: number) =>
  n === 1 ? 'a campaign another organization runs' : `${n} campaigns other organizations run`;

export const LAYERS_COPY = {
  tab: 'Layers',

  // ── The tree ──────────────────────────────────────────────────────────────────────────────────
  layersTitle: 'Layers',
  structureSubtitle:
    'Where this campaign sits, and every layer under it with its full total, including supporters who hide theirs.',
  thisCampaign: 'This campaign',
  addAbove: 'Add a layer above',
  addBelow: 'Add a layer below',
  colCampaign: 'Campaign',
  colRaised: 'Raised',
  colDonations: 'Donations',
  colState: 'State',
  colPublic: 'Public page',
  publicLive: 'Live',
  publicOff: 'Not public',
  layerCount: (count: number) => (count === 1 ? '1 layer' : `${count} layers`),
  otherLayers: (count: number) => (count === 1 ? '1 other layer' : `${count} other layers`),
  showMore: (count: number) => `Show ${count} more`,
  showLayers: (campaign: string) => `Show the layers under ${campaign}`,
  hideLayers: (campaign: string) => `Hide the layers under ${campaign}`,
  donations: (count: number) => (count === 1 ? '1 donation' : `${count} donations`),
  runBy: (kind: string | null | undefined) =>
    kind === 'group'
      ? 'Run by a group'
      : kind === 'person'
        ? 'Run by someone else'
        : 'Run by another organization',
  hiddenPublicly: 'Hidden publicly',
  anotherCampaign: 'Another campaign',
  editorLine: 'You can view this campaign’s layers. Its owners can add, move or remove them.',
  unpublishedAbove: (parent: string) =>
    `${parent} isn’t published yet, so this campaign’s total won’t count publicly above it until it is.`,
  layersEmptyTitle: 'No layers yet',
  layersEmptyBody:
    'Add a campaign under this one, like a team under a league. For a team that shares this campaign’s page, use Teams on the Campaign tab.',
  fundedHint: 'Has donations',
  removeVisible: 'Remove',
  moveAction: 'Move to another campaign',
  removeFrom: (parent: string) => `Remove from ${parent}`,
  rowActions: (campaign: string) => `Actions for ${campaign}`,

  // ── The campaign card's level (decision 3, FCM-BE56) ──────────────────────────────────────────
  cardTitle: 'Campaign card',
  cardLine: (level: string) => `Pages under this campaign show ${possessive(level)} card.`,
  cardChange: 'Change',
  cardMarker: 'Shown on cards',

  // ── What to call the campaigns inside it (FCM-WEB59: D-FCM-52, RULED 2026-10-22 07:19 PDT) ─────
  layerNameTitle: 'What to call the campaigns inside it',
  layerNameSubtitle:
    'The name this campaign’s pages use for them, like “Teams” for a league or “Games” for a season.',
  layerNameSingular: 'Singular',
  layerNamePlural: 'Plural',
  /** D-FCM-50's default, the words an empty pair leaves the pages with: each field's placeholder. */
  layerNameSingularDefault: 'Campaign',
  layerNamePluralDefault: 'Campaigns',
  layerNameEmptyMeans: 'Leave both empty to use “Campaign” and “Campaigns”.',
  layerNameSave: 'Save',
  layerNameSaved: 'Name saved.',

  // ── Leaving a campaign that can never come back (Q3) ──────────────────────────────────────────
  leaveNotice: (parent: string) =>
    `No one can bring ${parent} back, so this campaign may leave it.`,
  leaveAction: 'Leave',
  leaveTitle: (parent: string) => `Leave ${parent}?`,
  leaveBody: (parent: string) =>
    `This campaign becomes a campaign of its own, and its whole history leaves ${possessive(parent)} totals. You can’t undo this.`,
  leftToast: (parent: string) => `Left ${parent}.`,

  // ── Add a layer below ─────────────────────────────────────────────────────────────────────────
  addBelowTitleOn: (campaign: string) => `Add a layer below ${campaign}`,
  whatToAdd: 'What to add',
  optNew: 'A new campaign',
  optNewDesc: 'It starts as a draft. Publish it when it’s ready.',
  optYours: 'A campaign you run',
  optYoursDesc: 'It becomes a layer right away.',
  optOthers: 'A campaign someone else runs',
  optOthersDesc: 'Its organizers get an invitation to accept.',
  nameLabel: 'Campaign name',
  raisesForLike: (charities: string, campaign: string) =>
    `It raises for ${charities}, like ${campaign}.`,
  picksOwnFromList: (campaign: string) =>
    `It picks its own charities, from ${possessive(campaign)} list.`,
  yoursLabel: 'Your campaigns',
  candidatesTaken: 'Already part of another campaign',
  candidatePartOf: (campaign: string) => `Part of ${campaign}`,
  noYours: 'You don’t run another campaign that could go here.',
  linkLabel: 'Campaign link',
  linkHelp: 'Paste the link to its public campaign page.',
  organizedBy: (organization: string) => `Organized by ${organization}`,
  inviteNote:
    'If its organizers accept, it becomes a layer of this campaign: its total counts in this campaign’s totals, and it ends when this campaign ends.',
  addLayerConfirm: 'Add layer',
  createInvitation: 'Create invitation',
  layerAdded: 'Layer added.',

  // ── Add a layer above ─────────────────────────────────────────────────────────────────────────
  addAboveBody: (campaign: string) =>
    `${campaign} becomes part of a new campaign. The new campaign starts as a draft; publish it when it’s ready.`,
  letsPagesPickLike: (campaign: string) =>
    `It lets each page and layer under it pick its own charities, like ${campaign}.`,
  addAboveConfirm: 'Add layer above',
  layerAddedAbove: 'Layer added above.',

  // ── Move and remove (the parent's side, Q5) ───────────────────────────────────────────────────
  moveTitle: (layer: string) => `Move ${layer}`,
  moveUnder: 'Move it under',
  moveBody: (campaign: string) =>
    `Its whole history moves with it: it leaves ${possessive(campaign)} totals and counts in the new campaign’s.`,
  moveConfirm: 'Move layer',
  layerMoved: 'Layer moved.',
  removeTitle: 'Remove this layer?',
  removeBody: (layer: string, campaign: string) =>
    `${layer} becomes a campaign of its own, and its whole history leaves ${possessive(campaign)} totals.`,
  // Decision 2 (RULED 2026-10-06 22:49 PDT, "b) yes"): a removed campaign comes back only by this
  // campaign's invitation or approval, never by joining automatically.
  removeBodyOther: (layer: string, campaign: string) =>
    `${layer} becomes a campaign of its own, and its whole history leaves ${possessive(campaign)} totals. It comes back only if this campaign’s organizers invite it again or approve its request to join.`,
  removeConfirm: 'Remove layer',
  layerRemoved: 'Layer removed.',

  // ── Invitations (FCM-BE34) ────────────────────────────────────────────────────────────────────
  invitedTitle: 'Invited campaigns',
  invitedSubtitle: 'Campaigns someone else runs that you invited to be layers of this one.',
  invitedOn: (date: string) => `Invited ${date}`,
  revokeBody: (layer: string) => `${possessive(layer)} organizers can no longer accept it.`,
  inviteTitle: (parent: string) => `Invitation to join ${parent}`,
  inviteFrom: (organization: string, date: string) => `From ${organization} · Expires ${date}`,
  inviteIf: 'If you accept:',
  /**
   * The six things accepting means (plan §FC-16.2's five, in plain words, widened by decisions 1 and 9
   * and revision 2's F2). Term 5's first sentence names what {parent} requires: its own charities when it
   * picks them, or its list when its pages and layers pick; with no list there is nothing to name, and
   * only its second sentence stands.
   */
  inviteTerms: (
    parent: string,
    requires: { kind: 'campaign' | 'list'; charities: readonly string[] } | null,
  ) => [
    `This campaign’s total, including supporters who hide theirs, counts in ${possessive(parent)} totals, public and private, whatever this campaign’s “Show fundraising totals” switch says. The organizers of ${parent}, and of any campaign it is part of, now or later, see this campaign and each layer under it as one row with its total, even while that switch is off; its amount shows on ${possessive(parent)} public boards only while the switch is on.`,
    `This campaign’s page says “Part of ${parent}”, and names any campaign ${parent} is part of, now or later.`,
    `When ${parent} ends, by its deadline or its Close, this campaign and every page and layer under it end too, and they reopen only if it reopens.`,
    `Only ${possessive(parent)} organizers can remove this campaign, and only while ${parent} is open. You can’t take it out yourself, or deactivate or delete it while it’s part of ${parent}, unless ${parent} can never reopen. If it’s removed, its whole history leaves ${possessive(parent)} totals.`,
    `${
      requires === null || requires.charities.length === 0
        ? ''
        : requires.kind === 'campaign'
          ? `This campaign raises for ${series(requires.charities)}, as ${parent} requires. `
          : `This campaign picks its charities from ${possessive(parent)} list: ${series(requires.charities, 'or')}. `
    }Its charities can’t change while it’s part of ${parent}, and it can end before ${parent}, never after.`,
    `Otherwise, you keep control of this campaign’s settings, join rules, teams and supporters, and ${possessive(parent)} organizers get no access to them.`,
  ],
  inviteConsent: 'Accepting is your organization’s consent to all of this.',
  decline: 'Decline',
  accept: 'Accept invitation',
  accepted: 'Invitation accepted.',
  declined: 'Invitation declined.',

  // ── The Details tab: a Draft top its funded layers keep (FCM-BE31's fold, option A), a wrapper ─
  draftTopFootBody:
    'This draft can’t be deleted while a layer under it has donations or pledges still to be collected. Remove those layers first.',
  draftTopNotice:
    'This draft can’t be deleted while a layer under it has donations or pledges still to be collected. Remove those layers first, then delete the draft.',
  // The Deactivated top whose layers are funded, FCM-BE88's sibling: APPROVED as written,
  // 2026-10-19 07:20 PDT.
  deactivatedTopFootBody:
    'This campaign can’t be deleted while a layer under it has donations or pledges still to be collected. Remove those layers first.',
  deactivatedTopNotice:
    'This campaign can’t be deleted while a layer under it has donations or pledges still to be collected. Remove those layers first, then delete the campaign.',
  seeLayers: 'Go to Layers',
  wrapperRecipients:
    'Each page and layer under this campaign picks its own charities, so donors give through them, not to this campaign directly.',
  wrapperWhere: 'Who picks the charities is set on the Campaign tab.',
  wrapperBreakdown: 'See what was raised for each charity',
  linkedRecipients: 'This campaign’s charities can’t change while it’s linked to other campaigns.',
  switchAttached: (parent: string) =>
    `Its money still counts in ${possessive(parent)} totals, and the organizers above it still see it.`,

  // ── The campaign's end (FCM-BE35, FCM-BE61) ───────────────────────────────────────────────────
  deadlineTitle: 'Fundraising deadline',
  deadlineSubtitle:
    'When it passes, this campaign ends, and so does every page and layer under it.',
  deadlineLabel: 'Deadline (optional)',
  deadlinePlaceholder: 'No deadline',
  saveDeadline: 'Save deadline',
  deadlineSaved: 'Deadline saved.',
  endsOn: (when: string) => `Ends ${when}.`,
  endsWith: (when: string, parent: string) => `Ends ${when}, with ${parent}.`,
  earlierOnly: (parent: string) =>
    `${parent} ends then, so this campaign can end earlier, but not later.`,
  noDeadline: 'No deadline. This campaign ends when you close it.',
  // A no-break space keeps "11:59 PM" on one line (FCM-WEB34's phone walk).
  // Whose zone it is (the KCC dev build, 2026-10-31: a New York charity's deadline read "in Pacific
  // Time" with nothing on the screen to say why): the one a saved deadline was set in, the
  // organization's own for a new one, or, where the organization has none, the reader's.
  deadlineZone: (zone: string, whose: 'set' | 'org' | 'reader' = 'set') =>
    `It ends at 11:59\u00a0PM that day in ${zone}, ${
      whose === 'org'
        ? 'your organization’s time zone'
        : whose === 'reader'
          ? 'your own time zone'
          : 'the time zone it’s set in'
    }.`,
  changeZone: 'Change in Settings',
  endedOnDeadline: (date: string) =>
    `This campaign ended on its deadline, ${date}. To reopen it, move the deadline later or clear it.`,
  changeDeadline: 'Change deadline',
  endedWith: (parent: string) =>
    `This campaign ended with ${parent}. It reopens only if ${parent} reopens.`,
  endedWithGone: (parent: string) =>
    `This campaign ended with ${parent}. No one can bring ${parent} back, so you can leave it and then reopen this campaign.`,
  closeCascade: (others: number) =>
    `Every page and layer under it closes too${others > 0 ? `, including ${runByOthers(others)}` : ''}. Reopening reopens exactly what this closes.`,
  reopenCascade: (others: number) =>
    `Every page and layer that closed with it reopens too${others > 0 ? `, including ${runByOthers(others)}` : ''}.`,

  // ── Who picks the charities (FCM-BE37, Q1 and Q8) ─────────────────────────────────────────────
  whoTitle: 'Who picks the charities',
  whoFixed: (campaign: string, charity: string) =>
    `${campaign} raises for ${charity}, and so does every page and layer under it.`,
  whoBound: (parent: string, charities: string) =>
    `${parent} picks the charities, so this campaign raises for ${charities}, and so does every page and layer under it.`,
  whoCampaign: 'This campaign picks',
  whoCampaignDesc: 'Every page and layer under it raises for this campaign’s charities.',
  whoSupporters: 'Each page and layer picks',
  whoSupportersDesc:
    'Everyone who starts a page, and every layer, picks their own charities. Donors give through a team or page, not to this campaign directly.',
  listLabel: 'Limit their choice to a list (optional)',
  listHelp: 'Leave it empty to allow any charity, Cause fund or collection.',
  listHelpBound: (parent: string) => `Leave it empty to allow any of ${possessive(parent)} list.`,
  listFromAbove: (parent: string) => `${parent} limits the choice to its list:`,
  removeFromList: (name: string) => `Remove ${name}`,
  addToList: 'Add',
  whoLocksHint:
    'This locks when the first page or layer joins or the first gift arrives. Set it before inviting teams.',
  whoLocked: 'Locked now that this campaign has supporters, gifts or layers.',
  saveChoice: 'Save',
  choiceSaved: 'Charity choice saved.',

  // ── The campaign card's look (decision 3, FCM-BE56, FCM-BE66) ─────────────────────────────────
  cardStyleSubtitle:
    'How this campaign looks on the pages that show its card. It shows public totals only.',
  styleSchedule: 'Schedule',
  styleScheduleDesc: 'Its games or events on a timeline, each with its total and result.',
  /** REUSED: the apps' "Show all" pill under a board of campaigns (D-FCM-49 (3), approved; iOS
   * `CampaignLayerCopy.showAllCampaigns`), its last word the campaign's own name for them (D-FCM-50). */
  showAllCampaigns: (count: number, plural?: string | null) =>
    `Show all ${count.toLocaleString('en-US')} ${plural ?? 'campaigns'}`,
  styleLeaderboard: 'Leaderboard',
  styleLeaderboardDesc: 'Its top teams or pages, and the time left.',
  styleProgress: 'Progress',
  styleProgressDesc: 'Its total and goal.',
  stylePreview: 'Preview',
  styleSave: 'Save',
  styleEnded: 'Ended',
  styleDaysLeft: (days: number) => (days === 1 ? '1 day left' : `${days} days left`),
  styleRaisedOf: (goal: string) => `raised of ${goal} goal`,
  styleRaised: 'raised',

  // ── Teams (revision 2, F13) ───────────────────────────────────────────────────────────────────
  teamsHelper:
    'Teams share this campaign’s page. For a team with its own page, goal or organizers, add a layer.',

  // ── Toasts, with full stops (FCM-WEB28's ruling 5) ────────────────────────────────────────────
  layerFailed: 'Couldn’t change this campaign’s layers. Please try again.',
} as const;

/**
 * The backend's own sentences (FCM-BE31, `app/application/campaign_layers.py` at origin/development
 * `dc1e43d89`), where the ruled frames show the same fact before any refusal: the tree's foot under
 * another organization's campaign (frame 02c), a closed campaign's frozen layers (frame 12b), and the
 * foot card of an attached layer (frame 09). Verbatim, straight apostrophes as the server writes them.
 */
export const SERVER_WORDS = {
  removeOnlyByParent: (parent: string) =>
    `Only the organizers of ${parent} can remove this campaign from it.`,
  frozen: (ended: string) => `${ended} has ended, so the campaigns under it can't change.`,
  attached: (parent: string) =>
    `This campaign is part of ${parent}. Ask its organizers to remove it first.`,
  /** D-FCM-46 (RULED 2026-10-17 19:21 PDT): be#2671's `CARD_LEVEL_DETAIL`. */
  cardLevel:
    "Pages under this campaign can show its own card, or the card of a campaign it's part of.",
  /** FCM-BE37's `CHOICE_LOCKED_DETAIL`: the lock a campaign above holds too, which 11b's line omits. */
  choiceLocked:
    "Who picks this campaign's charities can't change once it has a supporter, a gift, or a campaign above or below it.",
  /**
   * FCM-BE35's `PART_OF_ENDED_CAMPAIGN_DETAIL` (`app/domain/campaign_ends.py`), its refusal of a publish,
   * a reactivate or a Reopen under an ended campaign (D-FCM-46: be#2644's item 22 #1, "stands as
   * written"): the state line of a draft a campaign above ended.
   */
  partOfEnded: 'This fundraiser is part of a campaign that has ended.',
} as const;
